// src/components/ForgotPassword.js
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import { setAuthData, setVerificationToken } from "../../slices/authSlice";
import "./forgotpass.css";

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [deviceId, setDeviceId] = useState("");
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaVerifiedStep2, setCaptchaVerifiedStep2] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const verificationToken = useSelector(
    (state) => state.auth.verificationToken
  );

  // Prevent browser back navigation in the forgot password flow
  useEffect(() => {
    window.history.pushState(null, null, window.location.href);
    window.onpopstate = () => {
      window.history.go(1);
    };
    return () => {
      window.onpopstate = null;
    };
  }, []);

  const generateRandomDeviceId = () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCaptchaChange = (value) => {
    setCaptchaVerified(true);
  };

  // New handler for captcha in Step 2
  const handleCaptchaChangeStep2 = (value) => {
    setCaptchaVerifiedStep2(true);
  };

  // Step 1: Send PIN to email
  const handleSendPin = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!email) {
      setErrors({ email: "Email is required." });
      return;
    }
    if (!validateEmail(email)) {
      setErrors({ email: "Enter a valid email." });
      return;
    }
    if (!captchaVerified) {
      setErrors({ captcha: "Please verify the captcha." });
      return;
    }
    const generatedDeviceId = generateRandomDeviceId();
    setDeviceId(generatedDeviceId);
    // Save deviceId and email (using eventId) in the Redux store
    dispatch(
      setAuthData({ token: null, deviceId: generatedDeviceId, eventId: email })
    );

    const url = config.API_FORGOT_PASSWORD_URL.replace(
      "{DeviceID}",
      generatedDeviceId
    ).replace("{UserID}", encodeURIComponent(email));

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      const result = data.Table[0];
      if (result.Status === "PIN Generated") {
        alert("PIN has been sent to your email.");
        setStep(2);
      } else if (result.Status === "Email not found") {
        setErrors({ general: "Email not found" });
      } else {
        setErrors({ general: "An error occurred. Please try again." });
      }
    } catch (error) {
      console.error("Forgot Password error:", error);
      setErrors({ general: "An error occurred. Please try again later." });
    }
  };

  // Step 2: Verify PIN/OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!otp) {
      setErrors({ otp: "Please enter the OTP." });
      return;
    }
    if (!captchaVerifiedStep2) {
      setErrors({ captcha: "Please verify the captcha." });
      return;
    }
    const url = config.API_FORGOT_PASSWORD_VERIFY_URL.replace(
      "{DeviceID}",
      deviceId
    )
      .replace("{UserID}", encodeURIComponent(email))
      .replace("{OTP}", encodeURIComponent(otp));
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (data.Table && data.Table.length > 0) {
        const result = data.Table[0];
        if (result.Status === "Success" && result.token) {
          dispatch(setVerificationToken(result.token));
          setStep(3);
        } else {
          setErrors({ otp: "PIN not correct" });
        }
      } else if (data === "Invalid OTP") {
        setErrors({ otp: "PIN not correct" });
      } else {
        setErrors({ otp: "An error occurred. Please try again." });
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setErrors({ otp: "An error occurred. Please try again later." });
    }
  };

  // Step 3: Update password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!password || !confirmPassword) {
      setErrors({ password: "Both password fields are required." });
      return;
    }
    if (password !== confirmPassword) {
      setErrors({ password: "Passwords do not match." });
      return;
    }
    const url = config.API_UPDATE_PASSWORD_URL.replace("{DeviceID}", deviceId)
      .replace("{UserID}", encodeURIComponent(email))
      .replace("{Password}", encodeURIComponent(password));
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${verificationToken}`,
        },
      });
      const data = await response.json();
      const result = data.Table[0];
      if (result.Status === "Success") {
        alert("Password has been updated successfully.");
        navigate("/login");
      } else {
        setErrors({ password: "Please try again later." });
      }
    } catch (error) {
      console.error("Update password error:", error);
      setErrors({ password: "An error occurred. Please try again later." });
    }
  };

  return (
    <div className="forgot-password-container">
      {step === 1 && (
        <div className="forgot-password-card">
          <form onSubmit={handleSendPin}>
            <h2>Forgot Password</h2>
            {errors.general && (
              <div className="error-message">{errors.general}</div>
            )}
            <div className="form-group">
              <label htmlFor="email">Enter your registered email:</label>
              <input
                type="email"
                id="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {errors.email && (
                <div className="error-message">{errors.email}</div>
              )}
              <ReCAPTCHA
                sitekey={config.RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
                className="g-recaptcha"
              />
              {errors.captcha && (
                <div className="error-message">{errors.captcha}</div>
              )}
            </div>
            <button type="submit" className="submit-button">
              Send PIN
            </button>
          </form>
        </div>
      )}

      {step === 2 && (
        <div className="forgot-password-card">
          <form onSubmit={handleVerifyOtp}>
            <h2>Verify PIN</h2>
            {errors.otp && <div className="error-message">{errors.otp}</div>}
            {errors.captcha && (
              <div className="error-message">{errors.captcha}</div>
            )}
            <div className="form-group">
              <label htmlFor="otp">Enter PIN:</label>
              <input
                type="text"
                id="otp"
                placeholder="PIN"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
            </div>
            <ReCAPTCHA
              sitekey={config.RECAPTCHA_SITE_KEY}
              onChange={handleCaptchaChangeStep2}
              className="g-recaptcha"
            />
            <button type="submit" className="submit-button">
              Verify PIN
            </button>
          </form>
        </div>
      )}

      {step === 3 && (
        <div className="forgot-password-card">
          <form onSubmit={handleUpdatePassword}>
            <h2>Update Password</h2>
            {errors.password && (
              <div className="error-message">{errors.password}</div>
            )}
            <div className="form-group">
              <label htmlFor="password">New Password:</label>
              <input
                type="password"
                id="password"
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password:</label>
              <input
                type="password"
                id="confirmPassword"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="submit-button">
              Update Password
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ForgotPassword;
